import type { ApiResponse, MentionMediaResponse } from '@fluenceur/types';
import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import api from '@/libs/axiosInstance';

/**
 * Fetch media data for a specific mention
 * @param mentionId - The ID of the mention
 * @returns Promise with mention media data
 */
const fetchMentionMedia = async (mentionId: string): Promise<MentionMediaResponse> => {
  const response = await api.get<ApiResponse<MentionMediaResponse>>(
    `/dashboard/mentions/${mentionId}/media`
  );
  return response.data.data as MentionMediaResponse;
};

const fetchFreshMentionMedia = async (mentionId: string): Promise<MentionMediaResponse> => {
  const response = await api.get<ApiResponse<MentionMediaResponse>>(
    `/dashboard/mentions/${mentionId}/media`,
    { params: { refresh: true } }
  );
  return response.data.data as MentionMediaResponse;
};

interface UseMentionMediaQueryOptions {
  mentionId: string | null | undefined;
  enabled?: boolean;
  refreshToken?: number;
}

const MENTION_MEDIA_QUERY_KEY_PREFIX = 'mentionMedia';

export const useMentionMediaQuery = ({
  mentionId,
  enabled = true,
  refreshToken = 0,
}: UseMentionMediaQueryOptions): UseQueryResult<MentionMediaResponse, Error> => {
  return useQuery<MentionMediaResponse, Error>({
    queryKey: [MENTION_MEDIA_QUERY_KEY_PREFIX, mentionId, refreshToken],
    queryFn: () => {
      if (!mentionId) {
        return Promise.reject(new Error('Mention ID is required.'));
      }
      if (refreshToken > 0) {
        return fetchFreshMentionMedia(mentionId);
      }
      return fetchMentionMedia(mentionId);
    },
    enabled: !!mentionId && enabled,
    staleTime: 1 * 60 * 1000, // 1 minute for faster updates
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
};

// Export the fetch function for use in other hooks
export { fetchMentionMedia };
